import { Fade, Tooltip as MuiTooltip } from '@mui/material'
import { PopperProps } from '@mui/material/Popper'
import cn from 'classnames'
import { styles, tooltipOffset } from 'ext-ui/components/Tooltip/Tooltip.style'
import { useResolvedDesignSystemComponent } from 'ext-ui/design-system/DesignSystemChannel'
import GeneratedLatestTooltip, {
  TooltipProps as GeneratedTooltipProps,
} from 'ext-ui/design-system/generated/latest/components/Tooltip/Tooltip'
import GeneratedLiveTooltip from 'ext-ui/design-system/generated/live/components/Tooltip/Tooltip'
import React, { ReactElement } from 'react'

import TooltipLegacy, {
  TooltipComponentProps,
  TooltipPlacement,
  TooltipProps,
} from './TooltipLegacy'

type GeneratedTooltipPlacement = NonNullable<GeneratedTooltipProps['placement']>

const generatedPlacements = new Set<GeneratedTooltipPlacement>([
  'top',
  'top-start',
  'top-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end',
  'right',
  'right-start',
  'right-end',
])

function generatedPlacementFor(
  placement: TooltipPlacement,
): GeneratedTooltipPlacement | null {
  return generatedPlacements.has(placement as GeneratedTooltipPlacement)
    ? (placement as GeneratedTooltipPlacement)
    : null
}

function fallbackChannelMarker(channel: 'live' | 'latest'): string {
  return channel === 'latest' ? 'latest-live-fallback' : 'live-legacy-fallback'
}

function isValidTooltipContent(
  tooltipContent: React.ReactNode,
): tooltipContent is string | number | React.ReactElement {
  return (
    tooltipContent !== undefined &&
    tooltipContent !== null &&
    (typeof tooltipContent === 'string' ||
      typeof tooltipContent === 'number' ||
      React.isValidElement(tooltipContent))
  )
}

function renderDesignSystemTooltip(
  Component: typeof GeneratedLiveTooltip,
  channel: 'live' | 'latest',
  props: TooltipComponentProps,
  fallbackTooltipId: string,
): ReactElement {
  const {
    open,
    tooltipContent,
    variant = 'default',
    placement = 'top',
    arrow = true,
    enterDelay = 100,
    exitDelay = 50,
    disableInteractive = true,
    className,
    children,
    id: tooltipIdProp,
    role: _rootRole,
    ...divProps
  } = props
  const tooltipId = tooltipIdProp || fallbackTooltipId
  const generatedPlacement = generatedPlacementFor(placement)
  const shouldUseLegacyFallback = variant !== 'default' || !generatedPlacement

  if (!isValidTooltipContent(tooltipContent)) {
    return <>{children}</>
  }

  if (shouldUseLegacyFallback) {
    return (
      <TooltipLegacy
        {...props}
        dataDesignSystemChannel={fallbackChannelMarker(channel)}
      />
    )
  }

  return (
    <MuiTooltip
      open={open}
      css={styles}
      className={className}
      title={tooltipContent}
      describeChild
      enterDelay={enterDelay}
      leaveDelay={exitDelay}
      slots={{
        transition: Fade,
        tooltip: React.forwardRef<HTMLDivElement>((slotProps, ref) => {
          const {
            className: slotClassName,
            id: slotTooltipId = tooltipId,
            ownerState: _ownerState,
            role: _slotRole,
            ...tooltipSlotProps
          } = slotProps as React.HTMLAttributes<HTMLDivElement> & {
            ownerState?: unknown
          }

          return (
            <div {...tooltipSlotProps} ref={ref} className={slotClassName}>
              <Component
                className={cn(className, generatedPlacement)}
                data-design-system-channel={channel}
                id={slotTooltipId}
                placement={generatedPlacement}
                showCarrot={arrow}
              >
                {tooltipContent}
              </Component>
            </div>
          )
        }),
      }}
      slotProps={{
        transition: {
          timeout: exitDelay === 0 ? 0 : 200,
        },
        popper: {
          id: undefined,
          role: undefined,
          sx: {
            zIndex: 1001,
          },
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: ({
                  placement,
                }: {
                  placement: PopperProps['placement']
                }) => tooltipOffset(placement),
              },
            },
          ],
        },
      }}
      arrow={arrow}
      id={tooltipId}
      placement={placement}
      disableInteractive={disableInteractive}
      onOpen={props.onOpen}
      onClose={props.onClose}
      {...divProps}
    >
      {children}
    </MuiTooltip>
  )
}

function LiveTooltip(props: TooltipComponentProps): ReactElement {
  const fallbackTooltipId = React.useId()

  return renderDesignSystemTooltip(
    GeneratedLiveTooltip,
    'live',
    props,
    fallbackTooltipId,
  )
}

function LatestTooltip(props: TooltipComponentProps): ReactElement {
  const fallbackTooltipId = React.useId()

  return renderDesignSystemTooltip(
    GeneratedLatestTooltip,
    'latest',
    props,
    fallbackTooltipId,
  )
}

function Tooltip(props: TooltipComponentProps): ReactElement {
  const ResolvedTooltip = useResolvedDesignSystemComponent(
    LiveTooltip,
    LatestTooltip,
  )

  return <ResolvedTooltip {...props} />
}

export default Tooltip
export type { TooltipPlacement, TooltipProps }
